import React from "react";

export function V2ProofSlide({ isActive: _ }: { isActive: boolean }) {
  return (
    <div className="flex flex-col h-full">
      <div className="mb-5 max-w-3xl">
        <h2 className="text-[32px] font-light tracking-tight mb-1 font-[family-name:var(--font-heading)]">
          Three verticals. One compounding skill set.
        </h2>
        <p className="text-[14px] text-[var(--color-fg-secondary)] leading-relaxed font-light">
          Each vertical partner feeds real industry data into the COSX engine. Every case processed adds new agent skills. These skills compound across industries.
        </p>
      </div>

      <div className="grid gap-6" style={{ gridTemplateColumns: "1.1fr 0.6fr 0.6fr" }}>
        {/* XENI — ~50% width */}
        <div className="flex flex-col">
          <div className="flex items-center gap-3 mb-3 pb-2 border-b border-[var(--color-border)]">
            <h3 className="text-[12px] font-[family-name:var(--font-mono)] font-bold text-[var(--color-fg)] uppercase tracking-[0.1em]">
              XENI
            </h3>
            <span className="text-[9px] font-[family-name:var(--font-mono)] font-bold uppercase tracking-[0.1em] px-2 py-0.5 rounded-full bg-emerald-500/20 text-emerald-600">
              LIVE
            </span>
          </div>
          <p className="text-[10px] font-[family-name:var(--font-mono)] uppercase tracking-wider text-[var(--color-fg-tertiary)] mb-2">
            Skills acquired:
          </p>
          <ul className="flex flex-col gap-1.5 mb-4">
            {([
              <><strong className="font-medium text-[var(--color-fg)]">Document extraction</strong> and analysis</>,
              <><strong className="font-medium text-[var(--color-fg)]">Cross-referencing</strong> supporting documents for conflicts</>,
              <><strong className="font-medium text-[var(--color-fg)]">Auto-filling</strong> UKVI forms from client data</>,
              <>Compliance verification against <strong className="font-medium text-[var(--color-fg)]">immigration rules</strong></>,
            ] as React.ReactNode[]).map((skill, i) => (
              <li
                key={i}
                className="text-[12px] text-[var(--color-fg-secondary)] leading-tight pl-4"
              >
                <span className="text-[var(--color-fg-tertiary)] -ml-4 mr-1.5 inline-block w-[10px]">&#8226;</span>
                {skill}
              </li>
            ))}
          </ul>
          <div className="pt-2 border-t border-[var(--color-border)] mt-auto h-[36px]">
            <p className="text-[9px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)]">15 beta clients (UK) · ProActive Legal (AU/NZ)</p>
            <p className="text-[9px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)]">200+ pipeline cases · 3 hours to minutes</p>
          </div>
          {/* Xeni logo placeholder */}
        </div>

        {/* ROBOLAB X — ~25% width */}
        <div className="flex flex-col">
          <div className="flex items-center gap-3 mb-3 pb-2 border-b border-[var(--color-border)]">
            <h3 className="text-[12px] font-[family-name:var(--font-mono)] font-bold text-[var(--color-fg)] uppercase tracking-[0.1em]">
              ROBOLAB X
            </h3>
            <span className="text-[9px] font-[family-name:var(--font-mono)] font-bold uppercase tracking-[0.1em] px-2 py-0.5 rounded-full bg-orange-500/10 text-orange-500">
              LAUNCHING
            </span>
          </div>
          <p className="text-[10px] font-[family-name:var(--font-mono)] uppercase tracking-wider text-[var(--color-fg-tertiary)] mb-2">
            Skills:
          </p>
          <ul className="flex flex-col gap-1.5 mb-4">
            {([
              <>4-layer <strong className="font-medium text-[var(--color-fg)]">content moderation</strong></>,
              <><strong className="font-medium text-[var(--color-fg)]">Age-appropriate</strong> filtering</>,
              <><strong className="font-medium text-[var(--color-fg)]">Multi-stakeholder</strong> access control</>,
            ] as React.ReactNode[]).map((skill, i) => (
              <li
                key={i}
                className="text-[12px] text-[var(--color-fg-secondary)] leading-tight pl-4"
              >
                <span className="text-[var(--color-fg-tertiary)] -ml-4 mr-1.5 inline-block w-[10px]">&#8226;</span>
                {skill}
              </li>
            ))}
          </ul>
          <div className="pt-2 border-t border-[var(--color-border)] mt-auto h-[36px]">
            <p className="text-[9px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)]">Exclusive partnership</p>
            <p className="text-[9px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)]">20 pilot students · 1-month MVP</p>
          </div>
        </div>

        {/* METAROOM — ~25% width */}
        <div className="flex flex-col">
          <div className="flex items-center gap-3 mb-3 pb-2 border-b border-[var(--color-border)]">
            <h3 className="text-[12px] font-[family-name:var(--font-mono)] font-bold text-[var(--color-fg)] uppercase tracking-[0.1em]">
              METAROOM
            </h3>
            <span className="text-[9px] font-[family-name:var(--font-mono)] font-bold uppercase tracking-[0.1em] px-2 py-0.5 rounded-full bg-gray-500/10 text-gray-500">
              IN DEVELOPMENT
            </span>
          </div>
          <p className="text-[10px] font-[family-name:var(--font-mono)] uppercase tracking-wider text-[var(--color-fg-tertiary)] mb-2">
            Skills:
          </p>
          <ul className="flex flex-col gap-1.5 mb-4">
            {([
              <><strong className="font-medium text-[var(--color-fg)]">MAS regulatory</strong> Q&amp;A</>,
              <>Marketing materials <strong className="font-medium text-[var(--color-fg)]">compliance review</strong></>,
            ] as React.ReactNode[]).map((skill, i) => (
              <li
                key={i}
                className="text-[12px] text-[var(--color-fg-secondary)] leading-tight pl-4"
              >
                <span className="text-[var(--color-fg-tertiary)] -ml-4 mr-1.5 inline-block w-[10px]">&#8226;</span>
                {skill}
              </li>
            ))}
          </ul>
          <div className="pt-2 border-t border-[var(--color-border)] mt-auto h-[36px]">
            <p className="text-[9px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)]">Singapore partner in pipeline</p>
            <p className="text-[9px] text-[var(--color-fg-tertiary)] font-[family-name:var(--font-mono)]">Fund admin and corporate secretarial</p>
          </div>
          {/* MetaRoom logo placeholder */}
        </div>
      </div>

      {/* Partner logos — outside grid so they don't affect card height */}
      <div className="grid gap-6 mt-5 items-start" style={{ gridTemplateColumns: "1.1fr 0.6fr 0.6fr" }}>
        <div>
          <img src="/xeni-logo-light.svg" alt="Xeni" className="h-[18px] w-auto opacity-50 dark:hidden" />
          <img src="/xeni-logo-dark.svg" alt="Xeni" className="h-[18px] w-auto opacity-50 hidden dark:block" />
        </div>
        <div><img src="/robolabx.avif" alt="Robolab X" className="h-[32px] w-auto opacity-50" /></div>
        <div className="flex items-center gap-1.5">
          <img src="/metaroom-icon.png" alt="" className="h-[22px] w-auto opacity-50" />
          <span className="text-[14px] font-bold text-[var(--color-fg)] opacity-50">Metaroom</span>
        </div>
      </div>

      <div className="text-left mt-auto mb-8 pb-4 border-b border-[var(--color-border)]">

        <p className="text-[12px] text-[var(--color-fg-secondary)] font-light">
          Skills transfer across verticals. Legal sharpens compliance. Education raises safety. <strong className="font-medium text-[var(--color-fg)]">Each one makes the others better.</strong>
        </p>
      </div>
    </div>
  );
}
